import ProductionDiffLine from '../ProductionDiffLine'
import { RadialGauge } from 'canvas-gauges'
import { pathOr } from 'ramda'
import { UNIT_PRESSURE } from '../Measurement'
import { Footer, useTheme, Status, Button } from '@iqueue/ui-kit'
import React, { useRef, useEffect, useState } from 'react'
import cn from 'classnames'
import { t } from '../../../hooks/useTranslate'
import SensorInfoModal from '../../SensorInfoModal'

export default function PressureCard({ title, summary, scope, className, ...props }) {
  const [width, setWidth] = useState(0)
  const { getCSSValue } = useTheme()
  const canvasRefs = useRef([])
  const gaugeRefs = useRef([])
  const [currentSensor, setCurrentSensor] = useState({})
  const [isOpened, setIsOpened] = useState(false)

  const pressureSensors = summary?.sensors?.filter(sensor =>
    (scope === 'PRESSURE_INPUT' && sensor.type === 'PRESSURE_INPUT') ||
    (scope === 'PRESSURE_OUTPUT' && sensor.type === 'PRESSURE_OUTPUT') ||
    (scope === 'PRESSURE_WELL' && sensor.type === 'PRESSURE_WELL') ||
    (scope === 'PRESSURE_PLUME' && sensor.type === 'PRESSURE_PLUME'),
  ) || []

  useEffect(() => {
    function check() {
      if (canvasRefs.current[0] && canvasRefs.current[0].parentNode) {
        const w = canvasRefs.current[0].parentNode.offsetWidth * 0.6
        setWidth(w)
      }
    }

    check()
    window.addEventListener('resize', check)
    return () => {
      window.removeEventListener('resize', check)
    }
  }, [])

  useEffect(() => {
    const w = Math.min(300, width)

    pressureSensors.forEach((sensor, index) => {
      const { limits, thresholds } = sensor
      const minValue = limits?.min || 0
      const maxValue = limits?.max || 100

      function getOptimalStep(range) {
        const steps = [1, 2, 5, 10, 20, 50, 100]
        for (let step of steps) {
          if (range / step <= 10) return step
        }
        return 1
      }

      const range = maxValue - minValue
      const step = getOptimalStep(range)
      const majorTicks = []
      let val = minValue
      while (val < maxValue) {
        majorTicks.push(Number(val.toFixed(2)).toString())
        val += step
      }
      majorTicks.push(Number(maxValue.toFixed(2)).toString())

      if (!gaugeRefs.current[index]) {
        gaugeRefs.current[index] = new RadialGauge({
          renderTo: canvasRefs.current[index],
          width: w,
          height: w,
          units: 'кГс/см²',
          minValue: minValue,
          maxValue: maxValue,
          majorTicks: majorTicks,
          highlights: [
            ...(thresholds?.critical || []).map(([from, to]) => ({ from, to, color: 'rgba(255, 0, 0, 0.7)' })),
            ...(thresholds?.warn || []).map(([from, to]) => ({ from, to, color: 'rgba(255, 255, 0, 0.7)' })),
          ],
          colorPlate: getCSSValue('--iq-darken'),
          colorMajorTicks: getCSSValue('--iq-color-secondary'),
          colorMinorTicks: getCSSValue('--iq-divider'),
          colorTitle: getCSSValue('--iq-color'),
          colorUnits: getCSSValue('--iq-color'),
          colorNumbers: getCSSValue('--iq-color-secondary'),
          colorNeedle: getCSSValue('--iq-accent'),
          colorNeedleEnd: getCSSValue('--iq-accent-secondary'),
          animationRule: 'elastic',
          animationDuration: 500,
          value: sensor.state.value || '',
          valueBox: true,
        })

        gaugeRefs.current[index].draw()
      }
    })

    return () => {
      gaugeRefs.current.forEach(gauge => {
        if (gauge) {
          gauge.destroy()
        }
      })
      gaugeRefs.current = []
    }
  }, [width, summary])

  return <div className={ cn('gas-and-oil-summary-card', className) } { ...props }>
    <div className="content">
      { pressureSensors.map((sensor, index) => {
        return <div key={ sensor.id || index } className="sensor-card">
          <div className="canvas-div">
            <canvas ref={ (el) => {
              canvasRefs.current[index] = el
            } }/>
          </div>
          <ProductionDiffLine
            center
            title={ t('averagePressure.daily') }
            current={ pathOr(0, [scope, 'summary', 'dailyValue'], summary) }
            past={ pathOr(0, [scope, 'summary', 'pastDayValue'], summary) }
            unit={ UNIT_PRESSURE }
          />
          <ProductionDiffLine
            center
            title={ t('averagePressure.monthly') }
            current={ pathOr(0, [scope, 'summary', 'monthlyValue'], summary) }
            past={ pathOr(0, [scope, 'summary', 'pastMonthValue'], summary) }
            unit={ UNIT_PRESSURE }
          />
          <ProductionDiffLine
            center
            title={ t('averagePressure.yearly') }
            current={ pathOr(0, [scope, 'summary', 'yearlyValue'], summary) }
            past={ pathOr(0, [scope, 'summary', 'pastYearValue'], summary) }
            unit={ UNIT_PRESSURE }
          />

          <Status
            style={ { marginTop: '0.8rem' } }
            inline={ sensor.status !== 'NO_SIGNAL' }
            weak
            warning={ sensor.status === 'TECH_WORKS' }
            error={ sensor.status === 'NO_SIGNAL' || sensor.status === 'SHORT_CIRCUIT' || sensor.status === 'NO_SENSOR_SIGNAL' || currentSensor.status === 'UNKNOWN_UNIT' || currentSensor.status === 'INVALID_DRIVER' }
          >
            { t(`status.sensor.${ sensor.status }`) }
          </Status>

          <Button
            onClick={ () => {
              setCurrentSensor(sensor)
              setIsOpened(true)
            } }
            className={ 'sensor-visibility' }
            icon={ 'visibility' }
          />

        </div>
      }) }

      <SensorInfoModal
        isOpened={ isOpened }
        title={ t('sensorInfo.modalTitle', { title }) }
        onClose={ () => setIsOpened(false) }
        footerActions={ [{ key: 'cancel', title: 'Закрыть', danger: true }] }
        currentSensor={ currentSensor }
        valueRenderer={ (value) => (value ? `${ value } кГс/см²` : 'Нет данных') }
      />

    </div>
    <Footer>
      <div className="iq-spacer">
        <b>{ title }</b>
      </div>
    </Footer>
  </div>
}
